import React from 'react';
import { cn } from '@/utils/cn';
import { Slot } from '@radix-ui/react-slot';
import { motion, HTMLMotionProps } from 'framer-motion';

type AnimationType = 'none' | 'scale' | 'bounce' | 'glow';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'ref'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  asChild?: boolean;
  animation?: AnimationType;
  children?: React.ReactNode;
}

// Animation configurations for framer-motion
const animations: Record<Exclude<AnimationType, 'none'>, {
  whileHover?: HTMLMotionProps<'button'>['whileHover'];
  whileTap?: HTMLMotionProps<'button'>['whileTap'];
  transition?: HTMLMotionProps<'button'>['transition'];
}> = {
  scale: {
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.95 },
    transition: { duration: 0.2 },
  },
  bounce: {
    whileHover: { y: -2 },
    whileTap: { y: 0 },
    transition: { duration: 0.2 },
  },
  glow: {
    whileHover: { boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)" },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.2 },
  },
};

// buttonVariants function for external use (e.g., Calendar component)
export function buttonVariants({
  variant = 'primary',
  size = 'md'
}: {
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size']
} = {}) {
  const variantClasses: Record<string, string> = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    ghost: 'btn-ghost',
    destructive: 'btn-destructive',
    link: 'btn-link',
  };

  const sizeClasses: Record<string, string> = {
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
    icon: 'btn-icon',
  };

  return cn('btn-base', variantClasses[variant || 'primary'], sizeClasses[size || 'md']);
}

const LoadingSpinner = ({ animated = false }: { animated?: boolean }) => {
  if (animated) {
    return (
      <motion.svg
        className="icon-with-text"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </motion.svg>
    );
  }

  return (
    <svg
      className="icon-with-text animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  asChild = false,
  animation = 'none',
  className,
  children,
  disabled,
  ...props
}, ref) => {
  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    ghost: 'btn-ghost',
    destructive: 'btn-destructive',
    link: 'btn-link',
  };

  const sizeClasses = {
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
    icon: 'btn-icon',
  };

  const buttonClassName = cn(
    'btn-base',
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  const isDisabled = disabled || isLoading;

  // If asChild, use Slot for composition
  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={buttonClassName}
        {...props}
      >
        {children}
      </Slot>
    );
  }

  // If animation is enabled, use motion.button
  if (animation !== 'none') {
    const animationConfig = animations[animation];
    return (
      <motion.button
        ref={ref}
        className={buttonClassName}
        disabled={isDisabled}
        whileHover={!isDisabled ? animationConfig.whileHover : undefined}
        whileTap={!isDisabled ? animationConfig.whileTap : undefined}
        transition={animationConfig.transition}
        {...(props as HTMLMotionProps<'button'>)}
      >
        {isLoading && <LoadingSpinner animated />}
        {children}
      </motion.button>
    );
  }

  // Default: regular button
  return (
    <button
      ref={ref}
      className={buttonClassName}
      disabled={isDisabled}
      {...props}
    >
      {isLoading && <LoadingSpinner />}
      {children}
    </button>
  );
});
Button.displayName = 'Button';
